import { useEnvContext } from "context/EnvContext";
import {
  APP_CAREPROVIDER,
  FILE_TABLE_ACTION_UPLOAD,
  FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
  FILE_TABLE_ACTION_UPLOAD_HIS,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
  TRACK_EVENTS,
} from "core/consts";
import { useExtractDocs } from "core/model/api/endpoints/extractDocs/context";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { processDocumentFile, validateFile } from "core/model/files";
import {
  ALLOWED_FILE_MIME_TYPES,
  MAX_UPLOAD_FILES,
} from "core/model/files/config";
import {
  AuctionRequest,
  FileCategory,
  FileHISStateful,
  FileState,
  Filev2,
  QueryProgress,
} from "core/types";
import { Modal } from "ds/ui";
import { Button, ButtonProps } from "ds/ui/button/button";
import {
  GeneralListAction,
  SetAction,
} from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import { useMedia } from "dsl/atoms/ResponsiveMedia";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import {
  FileFeatureData,
  useFilesFeatures,
} from "dsl/ecosystems/PatientFiles/useFilesFeature";
import { useIsEligiblePage } from "dsl/ecosystems/PatientFiles/useUploadFile";
import { getFileSource } from "dsl/ecosystems/PatientFiles/utils";
import { PRODUCT_TOURS, tourAttributes } from "dsl/molecules/useProductTour";
import { FileUploadSubmitHandler } from "dsl/organisms/Modals/FileUploadModal";
import { FileUpIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTracking } from "react-tracking";
import { useLoggedCareseekerConfig } from "reduxentities/hooks";
import { useTranslations } from "translations";
import { DragNDropScreen } from "./Screens/DragNDropScreen";
import {
  FileUploadScreen,
  UploadedFileResult,
} from "./Screens/FileUploadScreen";
import { HISFileUploadScreen } from "./Screens/HISFileUploadScreen";

export type ProcessedFile = {
  content: string;
  file: File;
};

export function useFileUpload() {
  const { trackEvent } = useTracking();
  const toast = useToast();
  const isEligiblePage = useIsEligiblePage();

  const translations = useTranslations();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [processedFiles, setProcessedFiles] = useState<ProcessedFile[]>([]);
  const [fileUploadProgress, setFileUploadProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );

  function handleClick() {
    if (inputRef.current) {
      inputRef.current.click();
    }
  }
  async function processFile(file: File | undefined) {
    try {
      if (!file) {
        throw new Error(translations.fileUploader.fileUploadError);
      }

      await validateFile({ file, isEligiblePage, trackEvent, translations });

      const content = await processDocumentFile(file);
      setProcessedFiles((prev) => [...prev, { content, file }]);
    } catch (error) {
      toast({ color: "danger", message: (error as Error).message });
    }
  }

  async function handleChange(files: FileList | null) {
    try {
      if (!files?.length) {
        throw new Error(
          translations.careproviderApp.settings.uploadFiles.errorMaxFiles({
            maxCount: String(MAX_UPLOAD_FILES),
          }),
        );
      }
      setFileUploadProgress(QUERY_PROGRESS_PENDING);
      await Promise.all(Array.from(files).map((file) => processFile(file)));
      setFileUploadProgress(QUERY_PROGRESS_SUCCEED);
    } catch (error) {
      setFileUploadProgress(QUERY_PROGRESS_FAILED);
      toast({ color: "danger", message: (error as Error).message });
    }
  }

  function reset() {
    if (inputRef.current) {
      inputRef.current.value = "";
      setProcessedFiles([]);
    }
  }

  function handleRemoveFile(index: number) {
    setProcessedFiles((prev) => prev.filter((_, i) => i !== index));
  }

  return {
    fileUploadProgress,
    handleChange,
    handleClick,
    handleRemoveFile,
    inputRef,
    processedFiles,
    reset,
  };
}

export function UploadFilesModal({
  action,
  allowedFileTypes,
  availableFiles,
  getFilesHISProgress,
  handleSubmit,
  handleUploadFilesHIS,
  isDisabled = true,
  isMessenger,
  mutationProgress,
  reset,
  setAction,
  unavailableFiles,
  uploadButtonProps,
}: {
  action: GeneralListAction<Filev2> | undefined;
  handleUploadFilesHIS: ReturnType<
    typeof useFilesFeatures
  >["handleUploadFilesHIS"];
  allowedFileTypes?: readonly string[] | string[];
  auctionRequest?: AuctionRequest;
  availableFiles: FileState[] | null;
  getFilesHISProgress: QueryProgress;
  handleSubmit: FileUploadSubmitHandler;
  isDisabled: boolean;
  isMessenger?: boolean;
  mutationProgress: QueryProgress;
  reset: () => void;
  setAction: SetAction<FileFeatureData>;
  unavailableFiles: FileHISStateful[] | null;
  uploadButtonProps?: Omit<ButtonProps<false>, "ref" | "onPress">;
}) {
  const { app } = useEnvContext();
  const { isTablet } = useMedia();
  const translations = useTranslations();
  const { trackEvent } = useTracking();
  const hasActiveOnPrem = useLoggedCareseekerConfig(
    "on_premise_archive_integration_enabled",
  );

  const {
    fileUploadProgress,
    handleChange,
    handleClick,
    handleRemoveFile,
    inputRef,
    processedFiles,
    reset: resetFile,
  } = useFileUpload();

  const isEligiblePage = useIsEligiblePage();
  const { isDischargeLetterActive, isDocsAndExtractActive } =
    useIsDocsAndExtractActive();
  const showExtractCheckbox =
    (isDocsAndExtractActive || isDischargeLetterActive) && !isEligiblePage;
  const { extractOcr } = useExtractDocs();

  const wasModalOpenRef = useRef(false);
  const savedCategoriesRef = useRef<(FileCategory | undefined)[]>([]);
  const isModalOpen = !!action?.type;

  async function submit(fileUploadData: UploadedFileResult[]) {
    if (!fileUploadData.length) return;

    reset();
    resetFile();
    savedCategoriesRef.current = [];

    await Promise.all(
      fileUploadData.map(async (fileData, i) => {
        const processedFile = processedFiles[i];

        if (!processedFile) {
          throw new Error("Processed file missing for upload");
        }

        trackEvent({
          cta: "upload",
          file_implementation: "HIS",
          modal: FILE_TABLE_ACTION_UPLOAD,
          name: TRACK_EVENTS.FILE_MODAL_CTA_CLICKED,
          source: getFileSource(fileData),
        });

        await handleSubmit(fileData, {
          onComplete: async (fileId) => {
            if (fileData.should_extract_on_upload && fileData.file) {
              await extractOcr.handleOcrForFile({
                file: fileData.file,
                fileCategory: fileData.category,
                id: fileId,
              });
            }
          },
        });
      }),
    );
  }

  const close = useCallback(() => {
    reset();
    resetFile();
    savedCategoriesRef.current = [];
  }, [reset, resetFile]);

  const handleSelectFileV2 = useCallback(() => {
    if (app === APP_CAREPROVIDER) {
      if (isTablet) {
        handleClick();
      } else {
        setAction({
          data: {} as FileFeatureData,
          type: FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
        });
      }
    } else {
      setAction({
        data: {} as FileFeatureData,
        type: hasActiveOnPrem
          ? FILE_TABLE_ACTION_UPLOAD_HIS
          : FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
      });

      trackEvent({
        file_implementation: "HIS",
        name: TRACK_EVENTS.SELECT_FILE_CTA_CLICKED,
      });
    }
  }, [app, isTablet, hasActiveOnPrem, trackEvent, handleClick, setAction]);

  useEffect(() => {
    if (processedFiles.length) {
      setAction({
        data: {} as FileFeatureData,
        type: FILE_TABLE_ACTION_UPLOAD,
      });
    }
  }, [processedFiles, setAction]);

  useEffect(() => {
    wasModalOpenRef.current = isModalOpen;
  }, [isModalOpen]);

  const renderModalContent = () => {
    switch (action?.type) {
      case FILE_TABLE_ACTION_UPLOAD_HIS:
        return (
          <HISFileUploadScreen
            availableFiles={availableFiles}
            getFilesHISProgress={getFilesHISProgress}
            handleUploadFilesHIS={handleUploadFilesHIS}
            isMessenger={isMessenger}
            mutationProgress={mutationProgress}
            setAction={setAction}
            unavailableFiles={unavailableFiles}
          />
        );
      case FILE_TABLE_ACTION_UPLOAD_DRAGNDROP:
        return (
          <DragNDropScreen
            handleChange={handleChange}
            processedFiles={processedFiles}
            setAction={setAction}
          />
        );
      case FILE_TABLE_ACTION_UPLOAD:
        if (!processedFiles.length) {
          return null;
        }
        return (
          <FileUploadScreen
            fileUploadProgress={fileUploadProgress}
            handleRemoveFile={handleRemoveFile}
            initialValues={processedFiles.map((processedFile) => ({
              category: null,
              content: processedFile.content,
              file: processedFile.file,
              fileName: processedFile.file.name,
              kis_document_id_string: action?.data.kis_document_id_string,
              should_extract_on_upload: showExtractCheckbox,
            }))}
            mutationProgress={mutationProgress}
            savedCategoriesRef={savedCategoriesRef}
            setAction={setAction}
            onSubmit={submit}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="flex h-10 flex-col">
        <Button
          id="upload_file"
          startContent={<FileUpIcon />}
          tour={tourAttributes({
            stepKey: PRODUCT_TOURS.files_page.steps.select_files.key,
            tourKey: PRODUCT_TOURS.files_page.key,
          })}
          {...uploadButtonProps}
          className="z-0 m-0"
          color="primary"
          isDisabled={isDisabled}
          onPress={handleSelectFileV2}
        >
          {uploadButtonProps?.children ??
            translations.fileSection.fileUploadComponent.uploadFile}
        </Button>

        <div className="flex flex-col">
          <input
            ref={inputRef}
            accept={(allowedFileTypes ?? ALLOWED_FILE_MIME_TYPES).join(",")}
            className="hidden"
            data-testid="file-input"
            disabled={isDisabled}
            name="name"
            type="file"
            onChange={(event) => handleChange(event.target.files)}
          />
        </div>
      </div>

      <Modal
        disableAnimation={wasModalOpenRef.current && isModalOpen}
        isDismissable={false}
        isOpen={isModalOpen}
        placement="center"
        size="3xl"
        onClose={() => {
          trackEvent({
            cta: "outside_modal",
            file_implementation: "HIS",
            modal: action?.type,
            name: TRACK_EVENTS.UPLOAD_MODAL_CLOSED,
          });
          close();
        }}
      >
        {renderModalContent()}
      </Modal>
    </>
  );
}
